import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Onboarding steps definition with arrow directions
const steps = [{
  id: 'create-angel',
  target: '#UnleashWizard',
  title: '👤 Create Your Angel',
  text: 'Start your SinShift journey by creating your Angel.',
  arrow: {
    from: 'bottom',
    to: 'top-right'
  }
}, {
  id: 'ritual',
  target: '#RitualBoard',
  title: '🎯 Complete Your First Ritual',
  text: 'Daily rituals unlock rewards and keep your Angel bond strong.',
  arrow: {
    from: 'left',
    to: 'right'
  }
}, {
  id: 'reward',
  target: '#RewardHub',
  title: '🎁 Claim Your Reward',
  text: 'Collect your first reward here.',
  arrow: {
    from: 'top',
    to: 'bottom'
  }
}, {
  id: 'chat',
  target: '#ChatSection',
  title: '💬 Start Chatting',
  text: 'Dive into a private chat with your Angel.',
  arrow: {
    from: 'right',
    to: 'left'
  }
}];
interface GuidedHelpOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (section: string) => void;
}
export const GuidedHelpOverlay: React.FC<GuidedHelpOverlayProps> = ({
  isOpen,
  onClose,
  onNavigate
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [targetRect, setTargetRect] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });
  const step = steps[activeStep];
  // Calculate arrow path based on direction
  const getArrowPath = (arrow: {
    from: string;
    to: string;
  }, rect: {
    top: number;
    left: number;
    width: number;
    height: number;
  }) => {
    // Default control points
    let startX, startY, endX, endY, controlX1, controlY1, controlX2, controlY2;
    // Set start and end points based on arrow direction
    switch (arrow.from) {
      case 'bottom':
        startX = rect.left + rect.width / 2;
        startY = rect.top + rect.height + 40;
        break;
      case 'top':
        startX = rect.left + rect.width / 2;
        startY = rect.top - 40;
        break;
      case 'left':
        startX = rect.left - 40;
        startY = rect.top + rect.height / 2;
        break;
      case 'right':
        startX = rect.left + rect.width + 40;
        startY = rect.top + rect.height / 2;
        break;
      default:
        startX = rect.left;
        startY = rect.top;
    }
    switch (arrow.to) {
      case 'top-right':
        endX = rect.left + rect.width * 0.75;
        endY = rect.top;
        break;
      case 'right':
        endX = rect.left + rect.width;
        endY = rect.top + rect.height / 2;
        break;
      case 'bottom':
        endX = rect.left + rect.width / 2;
        endY = rect.top + rect.height;
        break;
      case 'left':
        endX = rect.left;
        endY = rect.top + rect.height / 2;
        break;
      default:
        endX = rect.left + rect.width;
        endY = rect.top;
    }
    // Calculate control points for a nice curve
    if (arrow.from === 'bottom' && arrow.to === 'top-right') {
      controlX1 = startX + 80;
      controlY1 = startY;
      controlX2 = endX - 20;
      controlY2 = endY + 80;
    } else if (arrow.from === 'left' && arrow.to === 'right') {
      controlX1 = startX + (endX - startX) * 0.4;
      controlY1 = startY - 60;
      controlX2 = startX + (endX - startX) * 0.6;
      controlY2 = endY + 60;
    } else if (arrow.from === 'top' && arrow.to === 'bottom') {
      controlX1 = startX + 60;
      controlY1 = startY + (endY - startY) * 0.3;
      controlX2 = endX - 60;
      controlY2 = startY + (endY - startY) * 0.7;
    } else if (arrow.from === 'right' && arrow.to === 'left') {
      controlX1 = startX - (startX - endX) * 0.4;
      controlY1 = startY + 60;
      controlX2 = startX - (startX - endX) * 0.6;
      controlY2 = endY - 60;
    } else {
      // Default curve
      controlX1 = startX + (endX - startX) * 0.5;
      controlY1 = startY;
      controlX2 = endX - (endX - startX) * 0.5;
      controlY2 = endY;
    }
    return {
      path: `M${startX},${startY} C${controlX1},${controlY1} ${controlX2},${controlY2} ${endX},${endY}`,
      endX,
      endY
    };
  };
  useEffect(() => {
    if (!isOpen || !step) return;
    // Try to find the target element
    const targetElement = document.querySelector(step.target) || document.getElementById(step.id) || document.querySelector(`[data-section="${step.id}"]`);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    } else {
      // If element not found, center in viewport
      setTargetRect({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 150,
        width: 300,
        height: 200
      });
    }
    // Navigate to the relevant section if needed
    if (onNavigate) {
      if (step.id === 'create-angel') onNavigate('unleash');
      if (step.id === 'ritual') onNavigate('rituals');
      if (step.id === 'reward') onNavigate('rewards');
      if (step.id === 'chat') onNavigate('chat');
    }
  }, [activeStep, isOpen, step, onNavigate]);
  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      onClose();
    } else {
      setActiveStep(activeStep + 1);
    }
  };
  const handleBack = () => {
    if (activeStep === 0) {
      onClose();
    } else {
      setActiveStep(activeStep - 1);
    }
  };
  if (!isOpen || !step) return null;
  // Calculate arrow path
  const arrowInfo = getArrowPath(step.arrow, targetRect);
  // Calculate position for the instruction card
  // Position below the target by default, adjust if needed
  const cardTop = targetRect.top + targetRect.height + 20;
  const cardLeft = targetRect.left + targetRect.width / 2 - 150; // center the card
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-id="element-8207">
      {/* Highlight target */}
      <div className="absolute border-4 border-pink-500 rounded-xl shadow-xl animate-pulse-glow" style={{
      pointerEvents: 'none',
      top: targetRect.top - 8,
      left: targetRect.left - 8,
      width: targetRect.width + 16,
      height: targetRect.height + 16,
      transition: 'all 0.3s ease'
    }} data-id="element-8208" />
      {/* Curved arrow (SVG) */}
      <svg className="absolute inset-0 z-40 w-full h-full pointer-events-none" data-id="element-8209">
        <defs data-id="element-8210">
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto" data-id="element-8211">
            <polygon points="0 0, 10 3.5, 0 7" fill="#ec4899" data-id="element-8212" />
          </marker>
        </defs>
        <path d={arrowInfo.path} fill="transparent" stroke="#ec4899" strokeWidth="3" strokeLinecap="round" markerEnd="url(#arrowhead)" className="animate-dash" style={{
        strokeDasharray: 1000,
        strokeDashoffset: 1000,
        animation: 'dash 2s linear forwards'
      }} data-id="element-8213" />
      </svg>
      {/* Instruction card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm p-6 text-center absolute z-50 animate-pop" style={{
      top: cardTop > window.innerHeight - 200 ? targetRect.top - 200 : cardTop,
      left: Math.max(20, Math.min(cardLeft, window.innerWidth - 320))
    }} data-id="element-8214">
        <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white" data-id="element-8215">
          {step.title}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-8216">{step.text}</p>
        <div className="flex justify-between items-center" data-id="element-8217">
          <button onClick={handleBack} className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors" data-id="element-8218">
            <div className="flex items-center" data-id="element-8219">
              <ChevronLeft size={16} data-id="element-8220" />
              <span data-id="element-8221">Back</span>
            </div>
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-8222">
            Step {activeStep + 1} of {steps.length}
          </span>
          <button onClick={handleNext} className="px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600 transition-colors" data-id="element-8223">
            <div className="flex items-center" data-id="element-8224">
              <span data-id="element-8225">{activeStep === steps.length - 1 ? 'Finish' : 'Next'}</span>
              <ChevronRight size={16} data-id="element-8226" />
            </div>
          </button>
        </div>
      </div>
      {/* Close button for skipping the tour */}
      <button onClick={onClose} className="fixed top-4 right-4 bg-white dark:bg-gray-800 rounded-full p-2 shadow-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors" data-id="element-8227">
        Skip tour
      </button>
      {/* Add CSS animation for the arrow */}
      <style jsx data-id="element-8228">{`
        @keyframes dash {
          to {
            stroke-dashoffset: 0;
          }
        }
        .animate-dash {
          animation: dash 2s ease-in-out forwards;
        }
      `}</style>
    </div>;
};